/**
 * PoolScheduleGrid — la semaine du parc, vue gestionnaire.
 *
 * Plan `docs/labo/2026-09-13_GRILLE_HEBDO_ET_QR_PLAN.md` §A : la fiche salon promettait
 * une grille calendrier, le produit montrait une liste. Une ligne par véhicule, sept
 * jours, et les occupations rendues TELLES QUE le serveur les donne
 * (`GET /pool/schedule`, `backend/modules/pool/schedule_service.mjs`) — rien n'est
 * recalculé ici, pas même un motif.
 *
 * Trois gestes :
 *   - un jour libre → « Réserver pour un agent », prérempli sur ce véhicule et ce jour ;
 *   - un bloc → son détail (qui, jusqu'à quand, ou « fin inconnue » quand c'est le cas) ;
 *   - un bloc `reserved` → « Réassigner à un autre agent » (`PoolReassignDialog`, lot 2.4).
 *     Jamais sur un bloc `out` : une garde en cours se rend puis se re-réserve, une
 *     passation n'est pas un transfert — et le serveur le refuse en `BAD_STATE`.
 *
 * Honnêteté de démo : trois légendes seulement, parce qu'il n'y a que trois données —
 * réservé, sorti, immobilisé. Ni récurrence ni priorité : la grille le dit elle-même.
 *
 * Volumétrie : une seule requête pour la semaine. À 900 véhicules, une ligne par véhicule
 * n'est plus lisible — la réponse est le REGROUPEMENT PAR U.T. (`working_sites`) dès que
 * le périmètre en porte deux, avec une ligne d'en-tête qui résume la semaine en sept
 * cellules « occupés / total » et un fond d'intensité : on lit le taux d'occupation d'un
 * site avant de descendre aux véhicules. Les groupes sont repliés par défaut au-delà de
 * 25 véhicules — le détail est à un clic, il n'est pas imposé. La recherche du parent
 * filtre les lignes et déplie ce qui répond, sans jamais toucher aux comptes d'en-tête :
 * l'en-tête décrit le parc, la liste montre ce qu'on cherche.
 * Limite assumée : pas de virtualisation des lignes. Un groupe déplié de 400 véhicules
 * reste lourd ; à construire quand un vrai volume l'exigera (banc d'abord), le repli
 * étant déjà ce qui évite d'y arriver par accident.
 */
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, ChevronDown, X, Info, UserCog } from 'lucide-react';
import { poolScheduleAPI } from '../../api/pool.js';
import { useFormat } from '../../i18n/format.js';
import PoolReassignDialog from './PoolReassignDialog.jsx';

const DAYS = 7;

/** Au-delà, les groupes arrivent repliés : on résume d'abord, on détaille au clic. */
const EXPANDED_BY_DEFAULT_MAX = 25;

const KIND_STYLE = {
  reserved: 'bg-amber-200 border-amber-400 text-amber-900',
  out: 'bg-blue-200 border-blue-400 text-blue-900',
  blocked: 'bg-red-200 border-red-400 text-red-900',
};

/** Lundi 00:00 (heure locale) de la semaine qui contient `date`. */
export function startOfWeek(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

/** Créneau proposé pour un jour cliqué : 8 h – 10 h, ou la prochaine heure pleine si c'est aujourd'hui. */
export function proposedSlot(day, now = new Date()) {
  const start = new Date(day);
  start.setHours(8, 0, 0, 0);
  if (start.getTime() < now.getTime()) {
    start.setTime(now.getTime());
    start.setMinutes(0, 0, 0);
    start.setHours(start.getHours() + 1);
  }
  return { start, end: new Date(start.getTime() + 2 * 60 * 60_000) };
}

/**
 * Regroupe les véhicules par U.T., triés par nom ; les véhicules sans U.T. ferment la
 * marche (ce sont une exception, pas une catégorie). Aucune U.T. n'est inventée : un
 * groupe n'existe que s'il porte au moins un véhicule.
 */
export function groupVehiclesBySite(vehicles) {
  const groups = new Map();
  for (const v of vehicles || []) {
    const siteId = v.site_id ?? null;
    const key = siteId === null ? '__none__' : String(siteId);
    if (!groups.has(key)) groups.set(key, { site_id: siteId, site_name: v.site_name || null, vehicles: [] });
    const group = groups.get(key);
    if (!group.site_name && v.site_name) group.site_name = v.site_name;
    group.vehicles.push(v);
  }
  return Array.from(groups.values()).sort((a, b) => {
    if (a.site_id === null) return b.site_id === null ? 0 : 1;
    if (b.site_id === null) return -1;
    return (a.site_name || '').localeCompare(b.site_name || '', undefined, { sensitivity: 'base' });
  });
}

/**
 * Combien de véhicules sont occupés chaque jour — un élément par jour de `days`
 * (chacun étant un début de journée locale). Une occupation « touche » un jour dès
 * qu'elle le chevauche, si peu que ce soit : `start` nul vaut « depuis toujours »,
 * `end` nul vaut « fin inconnue », donc tous les jours suivants. On compte des
 * VÉHICULES, pas des occupations : deux sorties le même jour ne comptent qu'une fois.
 */
export function occupancyByDay(vehicles, days) {
  const list = vehicles || [];
  return (days || []).map((day) => {
    const dayStart = day.getTime();
    const dayEnd = addDays(day, 1).getTime();
    let occupied = 0;
    for (const v of list) {
      const touches = (v.occupations || []).some((occ) => {
        const start = occ.start == null ? -Infinity : new Date(occ.start).getTime();
        const end = occ.end == null ? Infinity : new Date(occ.end).getTime();
        return start < dayEnd && end > dayStart;
      });
      if (touches) occupied += 1;
    }
    return { occupied, total: list.length };
  });
}

/** Quatre paliers lisibles plutôt qu'un dégradé calculé : on lit une carte, pas une nuance. */
function heatClass(occupied, total) {
  if (!total || occupied === 0) return 'bg-white';
  const ratio = occupied / total;
  if (ratio >= 0.9) return 'bg-red-100';
  if (ratio >= 0.6) return 'bg-amber-100';
  return 'bg-emerald-50';
}

/** Recherche du parent : plaque, marque, modèle et libellés d'occupation (un nom d'agent). */
function matchesSearch(vehicle, needle) {
  const haystack = [
    vehicle.license_plate,
    vehicle.make,
    vehicle.model,
    ...(vehicle.occupations || []).map((occ) => occ.label),
  ];
  return haystack.some((value) => value && String(value).toLowerCase().includes(needle));
}

/**
 * Un bloc se réassigne s'il est une réservation pas encore prise. Le serveur ne
 * transfère qu'une `confirmed` ; un bloc `reserved` sans `status` (fixtures, vieux
 * serveur) est réputé confirmé — le serveur tranche de toute façon.
 */
export function canReassign(occ) {
  return occ?.kind === 'reserved' && occ.reservation_id != null && (occ.status == null || occ.status === 'confirmed');
}

function OccupationDetail({ vehicle, occ, onClose, onReassign, t, fmt }) {
  let period;
  if (occ.kind === 'out' && occ.end == null) {
    period = t('poolSchedule.detail.outOpenEnded', { from: fmt.dateTime(occ.start), planned: fmt.dateTime(occ.planned_end) });
  } else if (occ.kind === 'blocked' && occ.end == null) {
    period = t('poolSchedule.detail.blockedOpenEnded', { from: occ.start ? fmt.dateTime(occ.start) : '—' });
  } else {
    period = t('poolSchedule.detail.period', { from: occ.start ? fmt.dateTime(occ.start) : '—', to: fmt.dateTime(occ.end) });
  }
  return (
    <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm" role="region" aria-label={t('poolSchedule.detail.title')}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-gray-900">
            {vehicle.license_plate} · {t(`poolSchedule.kind.${occ.kind}`)}
          </p>
          <p className="mt-0.5 text-gray-700">{period}</p>
          {occ.kind === 'blocked' && occ.estimated_end && (
            <p className="text-xs text-gray-500">{t('poolSchedule.detail.estimatedEnd')}</p>
          )}
          {occ.label && (
            <p className="mt-1 text-gray-700">
              {occ.kind === 'blocked' ? occ.label : t('poolSchedule.detail.driver', { name: occ.label })}
            </p>
          )}
          {occ.destination_label && (
            <p className="text-gray-600">{t('poolSchedule.detail.destination', { destination: occ.destination_label })}</p>
          )}
          {canReassign(occ) && (
            <button
              type="button"
              onClick={onReassign}
              className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-blue-300 bg-white px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50"
            >
              <UserCog className="h-3.5 w-3.5" aria-hidden="true" />
              {t('poolSchedule.detail.reassign')}
            </button>
          )}
        </div>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label={t('poolSchedule.detail.close')}>
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/** Une ligne de véhicule : sept jours cliquables en fond, les occupations par-dessus. */
function VehicleRow({ vehicle, days, now, dayLabel, pct, nowPct, onReserveSlot, selected, setSelected, t }) {
  return (
    <div className="grid grid-cols-[14rem_1fr] border-b border-gray-100">
      <div className="min-w-0 px-2 py-2">
        <p className="truncate text-sm font-semibold text-gray-900">{vehicle.license_plate}</p>
        <p className="truncate text-xs text-gray-500">{[vehicle.make, vehicle.model].filter(Boolean).join(' ')}</p>
      </div>
      <div className="relative h-12">
        <div className="absolute inset-0 grid grid-cols-7">
          {days.map((d) => (
            <button
              key={d.toISOString()}
              type="button"
              onClick={() => {
                const slot = proposedSlot(d, now);
                onReserveSlot?.({ vehicleId: vehicle.vehicle_id, start: slot.start.toISOString(), end: slot.end.toISOString() });
              }}
              disabled={addDays(d, 1).getTime() <= now.getTime()}
              aria-label={t('poolSchedule.reserveDay', { plate: vehicle.license_plate, day: dayLabel(d) })}
              className={`border-l border-gray-100 hover:bg-emerald-50 disabled:cursor-default disabled:hover:bg-transparent ${[0, 6].includes(d.getDay()) ? 'bg-gray-50' : ''}`}
            />
          ))}
        </div>
        {nowPct != null && (
          <span className="pointer-events-none absolute inset-y-0 w-px bg-emerald-500" style={{ left: `${nowPct}%` }} aria-hidden="true" />
        )}
        {vehicle.occupations.map((occ, i) => {
          const left = pct(occ.start, 0);
          const right = pct(occ.end, 100);
          const openEnded = occ.end == null || (occ.kind === 'blocked' && occ.estimated_end);
          const isSelected = selected?.vehicle.vehicle_id === vehicle.vehicle_id && selected?.occ === occ;
          return (
            <button
              key={`${occ.kind}-${occ.reservation_id || occ.defect_id || i}`}
              type="button"
              onClick={() => setSelected(isSelected ? null : { vehicle, occ })}
              aria-label={`${vehicle.license_plate} · ${t(`poolSchedule.kind.${occ.kind}`)}${occ.label ? ` · ${occ.label}` : ''}`}
              className={`absolute top-1.5 h-9 overflow-hidden truncate rounded border px-1.5 text-left text-[11px] font-medium ${KIND_STYLE[occ.kind]} ${openEnded ? 'border-r-4 border-dashed' : ''} ${isSelected ? 'ring-2 ring-gray-700' : ''}`}
              style={{ left: `${left}%`, width: `${Math.max(right - left, 1.2)}%` }}
            >
              {occ.label || t(`poolSchedule.kind.${occ.kind}`)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** L'en-tête d'une U.T. : son nom, son effectif, et sa semaine en carte de chaleur. */
function SiteHeaderRow({ label, count, occupancy, days, expanded, onToggle, dayLabel, t }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      className="grid w-full grid-cols-[14rem_1fr] border-b border-gray-200 bg-gray-50 text-left hover:bg-gray-100"
    >
      <div className="flex min-w-0 items-center gap-1.5 px-2 py-1.5">
        {expanded
          ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-500" aria-hidden="true" />
          : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-gray-500" aria-hidden="true" />}
        <span className="truncate text-sm font-semibold text-gray-900">{label}</span>
        <span className="shrink-0 text-xs text-gray-500">{t('poolSchedule.groupVehicles', { count })}</span>
      </div>
      <div className="grid grid-cols-7">
        {days.map((d, i) => {
          const cell = occupancy[i] || { occupied: 0, total: count };
          return (
            <div
              key={d.toISOString()}
              data-day={d.toISOString()}
              title={t('poolSchedule.occupancyCell', { site: label, day: dayLabel(d), occupied: cell.occupied, total: cell.total })}
              className={`border-l border-gray-100 px-2 py-1.5 text-center text-[11px] font-medium text-gray-700 ${heatClass(cell.occupied, cell.total)}`}
            >
              {cell.occupied}/{cell.total}
            </div>
          );
        })}
      </div>
    </button>
  );
}

function PoolScheduleGrid({ siteId = null, sites = [], search = '', onReserveSlot, now: nowProp = null }) {
  const { t } = useTranslation();
  const { fmt, locale } = useFormat();
  const now = useMemo(() => (nowProp ? new Date(nowProp) : new Date()), [nowProp]);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(now));
  const [selected, setSelected] = useState(null); // { vehicle, occ }
  // Le dialogue de réassignation est ouvert sur la sélection courante (un bloc `reserved`).
  const [reassigning, setReassigning] = useState(false);
  // Clé de groupe → état voulu par l'utilisateur (un clic sur l'en-tête l'emporte sur le défaut).
  const [toggled, setToggled] = useState({});

  const from = weekStart;
  const to = addDays(weekStart, DAYS);
  const fromT = from.getTime();
  const spanT = to.getTime() - fromT;
  const days = useMemo(() => Array.from({ length: DAYS }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['pool-schedule', siteId, from.toISOString()],
    queryFn: () => poolScheduleAPI.get({
      from: from.toISOString(),
      to: to.toISOString(),
      siteId: siteId && siteId !== 'all' ? siteId : undefined,
    }),
    refetchInterval: 60_000,
  });
  const vehicles = (data?.data || data || {}).vehicles || [];

  const pct = (iso, fallback) => {
    if (iso == null) return fallback;
    return Math.max(0, Math.min(100, ((new Date(iso).getTime() - fromT) / spanT) * 100));
  };
  const nowPct = now.getTime() >= fromT && now.getTime() < to.getTime() ? pct(now.toISOString(), null) : null;
  const dayLabel = (d) => d.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' });

  // Le regroupement n'apparaît que s'il sépare quelque chose : une seule U.T. dans le
  // périmètre (le cas du filtre parent), et la grille reste la liste plate d'avant.
  const grouped = useMemo(() => groupVehiclesBySite(vehicles), [vehicles]);
  const useGroups = grouped.length >= 2;
  const needle = search.trim().toLowerCase();
  const searching = needle.length >= 2;
  const expandedByDefault = vehicles.length <= EXPANDED_BY_DEFAULT_MAX;

  const siteNames = useMemo(() => {
    const byId = new Map();
    for (const s of sites || []) byId.set(String(s.id), s.site_name);
    return byId;
  }, [sites]);

  const rows = useMemo(() => grouped.map((group) => {
    const key = group.site_id === null ? '__none__' : String(group.site_id);
    return {
      key,
      label: group.site_name || siteNames.get(key) || t('poolSchedule.noSite'),
      total: group.vehicles.length,
      // Les comptes d'en-tête décrivent le parc complet du groupe, jamais le filtre.
      occupancy: occupancyByDay(group.vehicles, days),
      shown: searching ? group.vehicles.filter((v) => matchesSearch(v, needle)) : group.vehicles,
    };
  }), [grouped, siteNames, days, searching, needle, t]);

  const visibleRows = searching ? rows.filter((r) => r.shown.length > 0) : rows;
  const flat = searching ? vehicles.filter((v) => matchesSearch(v, needle)) : vehicles;
  const nothingMatches = searching && (useGroups ? visibleRows.length === 0 : flat.length === 0);

  const rowProps = { days, now, dayLabel, pct, nowPct, onReserveSlot, selected, setSelected, t };

  return (
    <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => { setSelected(null); setWeekStart((w) => addDays(w, -DAYS)); }} className="rounded-lg border border-gray-300 p-1.5 hover:bg-gray-50" aria-label={t('poolSchedule.prevWeek')}>
            <ChevronLeft className="h-4 w-4" />
          </button>
          <p className="font-semibold text-gray-900">{t('poolSchedule.weekOf', { date: fmt.date(weekStart) })}</p>
          <button type="button" onClick={() => { setSelected(null); setWeekStart((w) => addDays(w, DAYS)); }} className="rounded-lg border border-gray-300 p-1.5 hover:bg-gray-50" aria-label={t('poolSchedule.nextWeek')}>
            <ChevronRight className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => { setSelected(null); setWeekStart(startOfWeek(now)); }} className="rounded-lg border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50">
            {t('poolSchedule.thisWeek')}
          </button>
        </div>
        <ul className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
          {['reserved', 'out', 'blocked'].map((k) => (
            <li key={k} className="flex items-center gap-1.5">
              <span className={`inline-block h-3 w-5 rounded border ${KIND_STYLE[k]}`} /> {t(`poolSchedule.kind.${k}`)}
            </li>
          ))}
          <li className="flex items-center gap-1.5">
            <span className="inline-block h-3 w-5 rounded border border-gray-200 bg-white" /> {t('poolSchedule.legendFree')}
          </li>
        </ul>
      </div>

      {error && (
        <p className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {t('poolSchedule.errors.loadFailed', { message: error.message })}
        </p>
      )}

      {isLoading ? (
        <p className="py-6 text-center text-sm text-gray-500">{t('poolSchedule.loading')}</p>
      ) : vehicles.length === 0 ? (
        <p className="py-6 text-center text-sm text-gray-500">{t('poolSchedule.empty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[860px]">
            <div className="grid grid-cols-[14rem_1fr] border-b border-gray-200 text-xs font-medium text-gray-600">
              <div className="px-2 py-1.5">{t('poolSchedule.vehicle')}</div>
              <div className="grid grid-cols-7">
                {days.map((d) => (
                  <div key={d.toISOString()} className={`border-l border-gray-100 px-2 py-1.5 ${[0, 6].includes(d.getDay()) ? 'bg-gray-50' : ''}`}>
                    {dayLabel(d)}
                  </div>
                ))}
              </div>
            </div>

            {nothingMatches && (
              <p className="py-6 text-center text-sm text-gray-500">{t('poolSchedule.searchEmpty', { search: search.trim() })}</p>
            )}

            {useGroups
              ? visibleRows.map((row) => {
                // Une recherche en cours déplie d'office ce qui répond : on ne cache pas
                // un résultat derrière un chevron.
                const expanded = searching || (toggled[row.key] ?? expandedByDefault);
                return (
                  <div key={row.key}>
                    <SiteHeaderRow
                      label={row.label}
                      count={row.total}
                      occupancy={row.occupancy}
                      days={days}
                      expanded={expanded}
                      onToggle={() => setToggled((prev) => ({ ...prev, [row.key]: !expanded }))}
                      dayLabel={dayLabel}
                      t={t}
                    />
                    {expanded && row.shown.map((v) => (
                      <VehicleRow key={v.vehicle_id} vehicle={v} {...rowProps} />
                    ))}
                  </div>
                );
              })
              : flat.map((v) => <VehicleRow key={v.vehicle_id} vehicle={v} {...rowProps} />)}
          </div>
        </div>
      )}

      {selected && (
        <OccupationDetail
          vehicle={selected.vehicle}
          occ={selected.occ}
          onClose={() => { setSelected(null); setReassigning(false); }}
          onReassign={() => setReassigning(true)}
          t={t}
          fmt={fmt}
        />
      )}

      {reassigning && selected && canReassign(selected.occ) && (
        <PoolReassignDialog
          reservationId={selected.occ.reservation_id}
          currentDriverId={selected.occ.driver_id ?? null}
          currentDriverLabel={selected.occ.label || null}
          summary={`${selected.vehicle.license_plate} · ${t('poolSchedule.detail.period', { from: selected.occ.start ? fmt.dateTime(selected.occ.start) : '—', to: fmt.dateTime(selected.occ.end) })}`}
          siteId={siteId && siteId !== 'all' ? siteId : null}
          onClose={() => setReassigning(false)}
          // Le bloc rafraîchi n'est plus le même objet : on ferme le détail plutôt que de
          // montrer l'ancien nom à côté du nouveau.
          onReassigned={() => setSelected(null)}
        />
      )}

      <p className="mt-3 flex items-start gap-1.5 text-xs text-gray-500">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {t('poolSchedule.scopeNote')}
      </p>
    </div>
  );
}

export default PoolScheduleGrid;
